import { useContext } from 'react';
import type { CSSProperties } from 'react';

import { CheckoutContext } from '@/context/CheckoutContext';
import { BLACK_COLOR, VALID_DETAILS_CONTENT_COLORS } from '@/constants/templateConfig';
import { useIsMobile } from '@/hooks/useIsMobile';
import { getCheckoutTemplate } from '@/templates/TemplateRegistry';

/**
 * Reactive: does the active template paint the publisher hero behind its
 * Details surface at the current viewport?
 *
 * Single source of truth for "is there a busy image behind the text the user
 * is reading right now?" Used by Details + every leaf inside it (PriceDetails,
 * TotalPrice, PurchasedItems, PlayerDetails) so they can pick a legible color
 * without each component re-deriving the same condition (and drifting).
 *
 * Delegates to the active template's `shouldShowBackground` method so the
 * same decision flows through `hasPublisherHaveBackgroundImage`, this hook,
 * the skeleton dispatcher, and any future consumer. Re-runs on resize /
 * orientation change via `useIsMobile`.
 */
export const useHasDetailsBackground = (): boolean => {
  const { boot, template } = useContext(CheckoutContext);
  const viewport = useIsMobile() ? 'mobile' : 'desktop';
  return getCheckoutTemplate(template.type).shouldShowBackground(viewport, boot?.checkoutTheme);
};

const isValidColor = (color: string): boolean => {
  return VALID_DETAILS_CONTENT_COLORS.includes(color.toLowerCase());
};

export const useDetailsContentColor = (): CSSProperties => {
  const { boot } = useContext(CheckoutContext);
  const hasBackground = useHasDetailsBackground();

  if (!hasBackground) {
    return { color: BLACK_COLOR };
  }

  const publisherTextColor = boot?.checkoutTheme?.textColor;
  if (publisherTextColor && isValidColor(publisherTextColor)) {
    return {
      color: publisherTextColor,
    };
  }

  return { color: BLACK_COLOR };
};
